import { useSession } from 'next-auth/react';
import { useCallback, useRef } from 'react';

interface FetchOptions extends RequestInit {
  headers?: HeadersInit;
}

export function useAuthenticatedFetch() {
  const { data: session } = useSession();
  const token = (session as any)?.idToken;

  // Use ref to always access the latest token without causing re-renders
  const tokenRef = useRef(token);
  tokenRef.current = token;

  // Memoize fetchWithAuth to prevent recreating on every render
  // This fixes aggressive polling in components that use this hook with useEffect
  const fetchWithAuth = useCallback(async <T = unknown>(url: string, options?: FetchOptions): Promise<T> => {
    const currentToken = tokenRef.current;
    if (!currentToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options?.headers,
        'Authorization': `Bearer ${currentToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || `Request failed: ${response.status}`);
    }

    return response.json();
  }, []); // Empty deps - uses ref for token to stay stable

  return {
    fetchWithAuth,
    isAuthenticated: !!token
  };
}
